import React, { useState, forwardRef, Ref, ReactElement, useEffect, useId } from 'react';
import { NumericFormatProps, NumericFormat, NumberFormatValues } from 'react-number-format';
import classnames from 'classnames';
import '@eika/currency-input-style';
import InputLabel from './InputLabel';
import InputError from './InputError';

export interface CurrencyInputProps extends NumericFormatProps {
    /**
     * The default change event of Number Format.
     * Returns: {formattedValue: "100 000 kr", value: "100000", floatValue: 100000}  */
    onValueChange?: (values: NumberFormatValues) => void;

    /**
     * Fake onChange event to handle external libs, use this only when needed.
     */
    onChange?: (e: { type: string; target: { name: string; value: string | number } }) => void;

    /**
     * The input value
     */
    value?: string | number;
    /**
     * @default prefix + '0 ' + suffix
     */
    placeholder?: string;
    /**
     * Prefix text to show on the left side of input
     */
    prefix?: string;
    /**
     * Suffix text to show on the left side of input
     */
    suffix?: string;
    /**
     * Defines the number of decimals you want
     * @default 0.
     */
    decimalScale?: number;
    /**
     * Tells which decimal separator you want, default is ','
     */
    decimalSeparator?: string;
    /**
     * Allows the use of negative values
     * @default false.
     */
    allowNegative?: boolean;
    /**
     * Adds classnames to input field, default classes are: 'sgw-currency-input' and 'sgw-currency-input__error' when errors.
     */
    className?: string;
    /**
     * Wraps the input field with a label.
     */
    label?: string;
    /**
     * Adds a sublabel above the input.
     */
    sublabel?: string;
    /**
     * Adds error output below the input. Also used  to highlight errors by adding the class: 'sgw-currency-input__error' to the input field.
     */
    error?: boolean | string | ReactElement;
    name: string;
    /**
     * right align text
     * @default false
     */
    rightAlignText?: boolean;
}

const CurrencyInput = forwardRef(
    (
        {
            className,
            label,
            sublabel,
            name,
            value,
            error,
            onChange,
            onValueChange,
            prefix = '',
            suffix = ' kr',
            decimalScale = 0,
            decimalSeparator = ',',
            thousandSeparator = ' ',
            allowNegative = false,
            placeholder,
            rightAlignText = false,
            ...rest
        }: CurrencyInputProps,
        ref: Ref<HTMLInputElement>,
    ) => {
        const id = useId();
        const [currency, setCurrency] = useState(value);
        const changeEvent = () => {
            if (!onChange) return onValueChange;

            return (v: NumberFormatValues) => {
                setCurrency(v.floatValue);
                onChange({
                    type: 'text',
                    target: { name, value: v.value },
                });
            };
        };

        useEffect(() => {
            setCurrency(value ?? '');
        }, [value]);
        return (
            <div className={classnames({ 'sgw-currency-input__disabled': !!rest.disabled })}>
                <InputLabel label={label} sublabel={sublabel}>
                    <div
                        className={classnames(
                            'sgw-currency-input',
                            { 'sgw-currency-input__error': !!error },
                            { 'sgw-currency-input__disabled': !rest.disabled },
                            className,
                        )}
                    >
                        <NumericFormat
                            id={id}
                            className={classnames('sgw-currency-input--input', { 'right-align-text': rightAlignText })}
                            aria-invalid={!!error}
                            placeholder={placeholder ?? `${prefix} 0 ${suffix}`}
                            prefix={prefix}
                            suffix={suffix}
                            thousandSeparator={thousandSeparator}
                            decimalScale={decimalScale}
                            decimalSeparator={decimalSeparator}
                            allowNegative={allowNegative}
                            getInputRef={ref}
                            value={currency}
                            valueIsNumericString
                            onValueChange={changeEvent()}
                            inputMode="decimal"
                            {...rest}
                        />
                    </div>
                </InputLabel>
                {error && typeof error !== 'boolean' && <InputError error={error} />}
            </div>
        );
    },
);

export default CurrencyInput;
